import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface HeroContent {
  image_url?: string;
  video_url?: string | null;
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  _v?: string;
}
interface JoinContent {
  image_url?: string;
  label?: string;
  headline?: string;
  body?: string;
  _v?: string;
}

/**
 * Append the site_content row's updated_at as a query param so Storage CDN
 * cache doesn't keep serving the OLD image after the admin uploads a new
 * one. Without this, customers see stale hero photos until their cache TTL
 * expires.
 */
function bust(url: string | undefined, version: string | undefined): string | undefined {
  if (!url || !version) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(version)}`;
}

export function Home() {
  const [hero, setHero] = useState<HeroContent | null>(null);
  const [join, setJoin] = useState<JoinContent | null>(null);

  useEffect(() => {
    apiFetch<{ homepage_hero?: HeroContent; homepage_join_card?: JoinContent }>(
      '/api/catalog/site-content'
    )
      .then((d) => {
        setHero(d.homepage_hero ?? null);
        setJoin(d.homepage_join_card ?? null);
      })
      .catch(() => {
        // Fall back to defaults if the endpoint fails — homepage still renders.
      });
  }, []);

  const heroImage = bust(hero?.image_url, hero?._v);
  const heroVideo = bust(hero?.video_url ?? undefined, hero?._v) || null;
  const heroEyebrow = hero?.eyebrow || 'A NEW WAY TO DRESS';
  const heroHeadline = (hero?.headline || 'YOUR\nWARDROBE.\nON ROTATION.').split('\n');
  const heroSub = hero?.subheadline || 'ZERO COMMITMENT.';

  const joinImage = bust(join?.image_url, join?._v);
  const joinLabel = join?.label || 'JOIN THE ROTATION';
  const joinHeadline = (join?.headline || 'A SMARTER WAY\nTO DRESS.').split('\n');
  const joinBody = join?.body || 'More style. Less hassle. A wardrobe that moves with you.';

  return (
    <section className="screen">
      <div className="scroll">
        <div className="home-hero-wrap">
          <div className="home-hero">
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              {heroEyebrow}
            </div>
            <h1>
              {heroHeadline.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < heroHeadline.length - 1 && <br />}
                </span>
              ))}
            </h1>
            <div className="sub">{heroSub}</div>
            <Link to="/browse" className="cta-primary">
              <span>BROWSE PIECES</span>
              <span className="arrow">
                <Arrow />
              </span>
            </Link>
          </div>
          <div
            className="hero-img"
            style={{
              backgroundImage: heroImage
                ? `linear-gradient(135deg,rgba(0,0,0,.05),rgba(0,0,0,.2)),url('${heroImage}')`
                : 'linear-gradient(135deg,var(--bg-3,#eee),var(--card,#f5f5f5))',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {heroVideo && (
              <video
                src={heroVideo}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            )}
            <div className="play">
              <span className="dot">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
              ROTATING NOW
            </div>
          </div>
        </div>

        <div className="features">
          <Feature
            title={
              <>
                30 DAY
                <br />
                ROTATION
              </>
            }
            desc="Wear it for 30 days, then send it back."
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            >
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <path d="M21 4v5h-5" />
            </svg>
          </Feature>
          <Feature
            title={
              <>
                CREDIT
                <br />
                SYSTEM
              </>
            }
            desc="Access pieces using credits."
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <rect x="2" y="6" width="20" height="13" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </Feature>
          <Feature
            title={
              <>
                PREPAID
                <br />
                RETURN
                <br />
                LABEL
              </>
            }
            desc="Emailed at the end of every rotation."
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
              <path d="M3.3 7l8.7 5 8.7-5" />
              <path d="M12 22V12" />
            </svg>
          </Feature>
          <Feature
            title={
              <>
                KEEP IT
                <br />
                MOVING
              </>
            }
            desc="Refresh your wardrobe anytime."
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M16 8l-6 8-2-3" />
            </svg>
          </Feature>
        </div>

        <div className="home-cta-card">
          <div className="label">AVAILABLE NOW</div>
          <div className="h">Curated pieces ready to rotate.</div>
          <Link to="/browse" className="btn">
            BROWSE PIECES{' '}
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="scroll-down">
          <div className="circle">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        <div className="how-section">
          <div className="eyebrow">HOW IT WORKS</div>
          <h2>IT&apos;S SIMPLE.</h2>
          <div className="home-steps-grid">
            <Step num="01" title="PICK YOUR PIECES" desc="Explore and choose what you want.">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              >
                <path d="M12 3l-9 7 9 4 9-4-9-7z" />
                <path d="M3 17l9 4 9-4" />
              </svg>
            </Step>
            <Step num="02" title="WEAR FOR 30 DAYS" desc="Enjoy your pieces for a full month.">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M3 9h18M8 3v4M16 3v4" />
              </svg>
            </Step>
            <Step num="03" title="SEND THEM BACK" desc="Prepaid return label included.">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              >
                <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
                <path d="M3.3 7l8.7 5 8.7-5" />
                <path d="M12 22V12" />
              </svg>
            </Step>
            <Step num="04" title="REFRESH" desc="Choose something new and keep rotating.">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 4v5h-5" />
              </svg>
            </Step>
          </div>
          <Link to="/how-it-works" className="see-how">
            SEE HOW IT WORKS{' '}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div
          className="join-card"
          style={{
            backgroundImage: joinImage
              ? `linear-gradient(135deg,rgba(0,0,0,.25),rgba(0,0,0,.45)),url('${joinImage}')`
              : 'linear-gradient(135deg,var(--ink,#222),#444)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="label">{joinLabel}</div>
          <div className="h">
            {joinHeadline.map((line, i) => (
              <span key={i}>
                {line}
                {i < joinHeadline.length - 1 && <br />}
              </span>
            ))}
          </div>
          <div className="p">{joinBody}</div>
        </div>

        <div className="footer-social">
          <span>FOLLOW US</span>
          <div className="icons">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
            </svg>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.6 6.3a4.6 4.6 0 0 1-3-2 .5.5 0 0 0-.4-.3h-2.7a.5.5 0 0 0-.5.5v10.7a2.4 2.4 0 1 1-3-2.3.5.5 0 0 0 .4-.5V9.7a.5.5 0 0 0-.6-.5 5.7 5.7 0 1 0 6.5 5.6V9.7a7.4 7.4 0 0 0 3.7 1 .5.5 0 0 0 .5-.5V6.8a.5.5 0 0 0-.4-.5z" />
            </svg>
          </div>
        </div>
        <div className="footer-cta pad-bottom">
          <Link to="/browse" className="cta-full">
            <span>JOIN THE ROTATION</span>
            <Arrow />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Feature({
  title,
  desc,
  children
}: {
  title: React.ReactNode;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div className="feature">
      <div className="ic">{children}</div>
      <div className="title">{title}</div>
      <div className="desc">{desc}</div>
    </div>
  );
}

function Step({
  num,
  title,
  desc,
  children
}: {
  num: string;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div className="step">
      <div className="ic-tile">{children}</div>
      <div>
        <div className="num">{num}</div>
        <div className="ttl">{title}</div>
        <div className="dsc">{desc}</div>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
